import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, CheckCircle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

interface BudgetCategory {
  id: string;
  categoryName: string;
  allocatedAmount: string | number;
  spentAmount: number;
}

interface Budget {
  id: string;
  fiscalYear: string;
  totalAmount: string | number;
  status: "DRAFT" | "APPROVED";
  fileUrl?: string | null;
  approvedAt?: string | null;
  createdAt: string;
  categories: BudgetCategory[];
}

const statusVariant: Record<string, "default" | "info" | "warning" | "success"> = {
  DRAFT: "warning",
  APPROVED: "success",
};

export function BudgetsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Budget | null>(null);

  const { data: budgets = [], isLoading, isError } = useQuery({
    queryKey: ["budgets"],
    queryFn: async () => (await api.get<Budget[]>("/budgets")).data,
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/budgets/${id}/approve`, {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["budgets"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/budgets/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["budgets"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("finance.budgets")}</h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("finance.newBudget")}
        </button>
      </div>

      {isError ? (
        <QueryError />
      ) : isLoading ? (
        <div className="card p-6 text-slate-500">{t("common.loading")}</div>
      ) : budgets.length === 0 ? (
        <div className="card p-6 text-slate-500">—</div>
      ) : (
        <div className="space-y-4">
          {budgets.map((b) => (
            <div key={b.id} className="card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold">{b.fiscalYear}</span>
                  <Badge variant={statusVariant[b.status]}>{t(`finance.budgetStatus.${b.status}`, b.status)}</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-lg">{formatCurrency(b.totalAmount)}</span>
                  {b.status === "DRAFT" && (
                    <button
                      className="btn bg-green-50 text-green-700 hover:bg-green-100 text-xs flex items-center gap-1"
                      disabled={approveMutation.isPending}
                      onClick={() => approveMutation.mutate(b.id)}
                    >
                      <CheckCircle size={14} /> {t("finance.approve")}
                    </button>
                  )}
                  {b.fileUrl && (
                    <a
                      href={b.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary text-xs"
                    >
                      {t("finance.viewFile")}
                    </a>
                  )}
                  <AdminRecordActions
                    entityLabel={b.fiscalYear}
                    onEdit={() => setEditing(b)}
                    onDelete={() => deleteMutation.mutate(b.id)}
                    disabled={deleteMutation.isPending}
                  />
                </div>
              </div>

              {b.categories.length > 0 && (
                <div className="overflow-hidden rounded border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-1.5">{t("finance.category")}</th>
                        <th className="text-right px-3 py-1.5">{t("finance.allocatedAmount")}</th>
                        <th className="text-right px-3 py-1.5">{t("finance.percentOfTotal")}</th>
                        <th className="text-right px-3 py-1.5">{t("finance.spentAmount")}</th>
                        <th className="text-right px-3 py-1.5">{t("finance.remainingAmount")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {b.categories.map((c) => {
                        const allocated = Number(c.allocatedAmount);
                        const spent = Number(c.spentAmount) || 0;
                        const remaining = allocated - spent;
                        const overBudget = spent > allocated;
                        const pct =
                          Number(b.totalAmount) > 0 ? ((allocated / Number(b.totalAmount)) * 100).toFixed(1) : "0.0";
                        return (
                          <tr key={c.id} className="hover:bg-slate-50">
                            <td className="px-3 py-1.5">{c.categoryName}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{formatCurrency(c.allocatedAmount)}</td>
                            <td className="px-3 py-1.5 text-right text-slate-500">{pct}%</td>
                            <td className={`px-3 py-1.5 text-right font-medium ${overBudget ? "text-red-600" : ""}`}>
                              {formatCurrency(spent)}
                              {overBudget && <span className="ml-2"><Badge variant="danger">{t("finance.overBudget")}</Badge></span>}
                            </td>
                            <td className={`px-3 py-1.5 text-right ${remaining < 0 ? "text-red-600" : "text-slate-500"}`}>
                              {formatCurrency(remaining)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(open || editing) && (
        <BudgetModal
          key={editing?.id ?? "new"}
          editing={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

interface CategoryRow {
  categoryName: string;
  allocatedAmount: string;
}

function BudgetModal({ editing, onClose }: { editing: Budget | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isEdit = !!editing;

  const [form, setForm] = useState({
    fiscalYear: editing?.fiscalYear ?? "",
    totalAmount: editing != null ? String(editing.totalAmount) : "",
    fileUrl: editing?.fileUrl ?? "",
  });
  const [categories, setCategories] = useState<CategoryRow[]>(
    editing && editing.categories.length > 0
      ? editing.categories.map((c) => ({
          categoryName: c.categoryName,
          allocatedAmount: String(c.allocatedAmount),
        }))
      : [{ categoryName: "", allocatedAmount: "" }],
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        fiscalYear: form.fiscalYear,
        totalAmount: parseFloat(form.totalAmount),
        fileUrl: form.fileUrl || null,
        categories: categories
          .filter((c) => c.categoryName.trim())
          .map((c) => ({
            categoryName: c.categoryName,
            allocatedAmount: parseFloat(c.allocatedAmount) || 0,
          })),
      };
      return isEdit
        ? (await api.put(`/budgets/${editing!.id}`, payload)).data
        : (await api.post("/budgets", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["budgets"] });
      onClose();
      setError(null);
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : t("common.error");
      setError(msg);
    },
  });

  function addCategory() {
    setCategories((prev) => [...prev, { categoryName: "", allocatedAmount: "" }]);
  }

  function removeCategory(idx: number) {
    setCategories((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateCategory(idx: number, field: keyof CategoryRow, value: string) {
    setCategories((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? t("finance.budgets") : t("finance.newBudget")} size="lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mutation.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("finance.fiscalYear")}</label>
            <input
              className="input"
              placeholder="2026-2027"
              required
              pattern="\d{4}-\d{4}"
              value={form.fiscalYear}
              onChange={(e) => setForm({ ...form, fiscalYear: e.target.value })}
            />
            <p className="text-xs text-slate-500 mt-0.5">{t("finance.fiscalYearHint")}</p>
          </div>
          <div>
            <label className="label">{t("finance.totalAmount")} (USD)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              className="input"
              required
              value={form.totalAmount}
              onChange={(e) => setForm({ ...form, totalAmount: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className="label">{t("finance.fileUrl")} (https://…)</label>
          <input
            className="input"
            placeholder="https://..."
            value={form.fileUrl}
            onChange={(e) => setForm({ ...form, fileUrl: e.target.value })}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label mb-0">{t("finance.categoryAllocations")}</label>
            <button type="button" className="btn-secondary text-xs" onClick={addCategory}>
              <Plus size={12} /> {t("finance.addCategory")}
            </button>
          </div>
          <div className="space-y-2">
            {categories.map((c, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  className="input flex-1"
                  placeholder={t("finance.categoryName")}
                  value={c.categoryName}
                  onChange={(e) => updateCategory(idx, "categoryName", e.target.value)}
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input w-36"
                  placeholder="0.00"
                  value={c.allocatedAmount}
                  onChange={(e) => updateCategory(idx, "allocatedAmount", e.target.value)}
                />
                {categories.length > 1 && (
                  <button
                    type="button"
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                    onClick={() => removeCategory(idx)}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {error && <QueryError />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>
            {t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
